function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { PricingStates, OdataRawObjectAppType, TermsStrings } from '@shared/Models';
import { AzureEnvironmentSettings, AzureEnvironment } from '@shared/AzureEnvironmentSettings';
import { Constants, OfferType, allBillingCountries, PlanPricingType } from '@shared/utils/constants';
import FuturePriceTooltip from '@shared/components/futurePriceTooltip';
import { CustomMeterPricingMaxFractionDigits } from '@shared/components/customMeterPricing';
import { ButtonLink } from '@shared/components/ButtonLink';
import { isD365App, isD365BCApp, isPowerBIVisualsApp, isBagTransactable, isTransactApp, getPDPRoute, getProductByUrlKey } from '@shared/utils/appUtils';
import { getPrimaryProductUrl } from '@shared/utils/datamappingHelpers';
const fallbackCountryCode = 'US';
export function loadAppPricing(rawApp, pricesData) {
    if (!rawApp || rawApp.appType === OdataRawObjectAppType.private) {
        return rawApp;
    }
    const app = _object_spread({}, rawApp);
    const pricingData = pricesData[app.entityId];
    if (pricingData) {
        app.startingPrice = pricingData;
        if (!app.startingPrice.pricingData) {
            app.startingPrice.pricingData = PricingStates.NoPricingData;
        }
        if (pricingData.pricingBitmask) {
            Object.keys(pricingData.pricingBitmask).forEach((key)=>{
                if (!app[`${key}`]) {
                    app[`${key}`] = 0;
                }
                app[`${key}`] |= pricingData.pricingBitmask[`${key}`];
            });
        }
    } else {
        app.startingPrice = {
            pricingData: PricingStates.NoPricingData
        };
    }
    return app;
}
const mooncakeBillingCountries = [
    {
        countryCode: 'cn',
        currency: 'CNY',
        name: 'China'
    }
];
export const billingCountries = AzureEnvironmentSettings.environment === AzureEnvironment.Mooncake ? mooncakeBillingCountries : allBillingCountries;
export function getBillingCountryByCountryCode(countryCode) {
    return billingCountries.filter((item)=>item.countryCode.toLowerCase() === countryCode.toLowerCase())[0];
}
export function renderNotAvailablePriceUI(context) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "noAvailablePrice",
        children: [
            /*#__PURE__*/ _jsx("h6", {
                className: "c-heading-6",
                children: context.loc('Pricing_SoftwarePlanDetail_Title')
            }),
            /*#__PURE__*/ _jsx("p", {
                children: context.loc('Pricing_SoftwarePlanDetail_NotAvailableDescription')
            })
        ]
    });
}
export function getContactPublisherText(context) {
    return `<span class='contactPublisherText'>${context.loc('Pricing_ContactPublisher')}</span>`;
}
const DAYS_IN_MONTH = 30;
const DAYS_IN_YEAR = 365;
export const TermsStringsToDays = (term)=>{
    switch(term){
        case TermsStrings.Term1MString:
        case TermsStrings.MonthString:
            return DAYS_IN_MONTH;
        case TermsStrings.Term1YString:
        case TermsStrings.YearString:
            return DAYS_IN_YEAR;
        case TermsStrings.Term2YString:
            return DAYS_IN_YEAR * 2; // 365*2 years
        case TermsStrings.Term3YString:
            return DAYS_IN_YEAR * 3; // 365*3 years
        case TermsStrings.Term4YString:
            return DAYS_IN_YEAR * 4; // 365*4 years
        case TermsStrings.Term5YString:
            return DAYS_IN_YEAR * 5; // 365*5 years
        default:
            return DAYS_IN_MONTH;
    }
};
function getFreeTrialText({ context, asInlineHTML, hasFreeTrial, freeTrialTerm, freeDurationDays }) {
    if (!hasFreeTrial) {
        return '';
    }
    const months = freeDurationDays ? freeDurationDays / DAYS_IN_MONTH : TermsStringsToDays(freeTrialTerm) / DAYS_IN_MONTH;
    const singleMonthText = asInlineHTML ? context.locParams('FreeTrialFirstMonthFree', [
        '<span>',
        '</span>'
    ]) : context.loc('First_Month_Free_Trial', `First month free, then `);
    const multiMonthText = asInlineHTML ? context.locParams('FreeTrialFirstSomeMonthsFree', [
        '<span>',
        months.toString(),
        '</span>'
    ]) : context.locParams('First_Months_Free_Trial', [
        months.toString()
    ], `First ${months.toString()} months free, then`);
    return months === 1 ? singleMonthText : multiMonthText;
}
function getCustomMeterPricing(context, countryCode, currency, sku, price) {
    var _price_customMeter;
    if (!((_price_customMeter = price.customMeter) === null || _price_customMeter === void 0 ? void 0 : _price_customMeter.length)) {
        return '';
    }
    let customMeterPriceText = '';
    const customMeters = price.customMeter;
    let firstLine = true;
    for(let i = 0; i < customMeters.length; i++){
        if (customMeters[`${i}`].includedQuantity === '0') {
            continue;
        }
        if (firstLine) {
            customMeterPriceText += context.locParams('PricingInclude', [
                "<span id='includedPrice'>",
                ':</span><span>',
                customMeters[`${i}`].title + ': ' + customMeters[`${i}`].includedQuantity,
                '</span>'
            ]);
            firstLine = false;
        } else {
            customMeterPriceText += '<span>' + customMeters[`${i}`].title + ': ' + customMeters[`${i}`].includedQuantity + '</span>';
        }
    }
    for(let i = 0; i < customMeters.length; i++){
        const priceString = customMeters[`${i}`].rate.toLocaleString(countryCode, {
            style: 'currency',
            currency: sku.currencyCode || currency,
            maximumFractionDigits: CustomMeterPricingMaxFractionDigits
        });
        if (i === 0) {
            customMeterPriceText += context.locParams('PricingPlus', [
                "<span id='plusPrice'>",
                ':</span><span>',
                customMeters[`${i}`].title + ': ' + priceString,
                customMeters[`${i}`].unit.toLowerCase(),
                '</span>'
            ]);
        } else {
            customMeterPriceText += '<span>' + customMeters[`${i}`].title + ': ' + priceString + ' ' + customMeters[`${i}`].unit.toLowerCase() + '</span>';
        }
    }
    return customMeterPriceText;
}
function getLocaleLanguage({ locale }) {
    const localeLanguage = (locale === null || locale === void 0 ? void 0 : locale.includes('-')) ? locale.split('-')[0] : Constants.DefaultLocale.split('-')[0];
    return localeLanguage;
}
function getCountryCodeRegion({ countryCode }) {
    const countryCodeRegion = countryCode ? countryCode.includes('-') ? countryCode.split('-')[1] : countryCode : fallbackCountryCode;
    return countryCodeRegion;
}
export function getPricingLocale({ locale, countryCode }) {
    const localeLanguage = getLocaleLanguage({
        locale
    });
    const countryCodeRegion = getCountryCodeRegion({
        countryCode
    });
    const localeString = `${localeLanguage.toLowerCase()}-${countryCodeRegion.toUpperCase()}`;
    return localeString;
}
export function getPriceWithCurrencySymbol(price, countryCode, currency, maximumFractionDigits = 5, locale) {
    const localeString = getPricingLocale({
        locale,
        countryCode
    });
    let priceString = price.toLocaleString(localeString, {
        style: 'currency',
        currency,
        maximumFractionDigits
    });
    // If the currency code is not converted to a symbol, then we want to display a space between the text and number.
    // For ex, PHP11.166 should be PHP 11.166. toLocaleString(..) does not support formatting. So we shd do this manually.
    // We first find out if the currency is present in the resulting priceString. This makes sure we aren't inserting space in a symbol.
    // We find out the position to insert a space (currencyValuePosition). We make sure that we are inserting
    // a space in between the price string - we don't want to insert a space at the end. Also, we don't want to insert a space if we already have one.
    const currencyCodePosition = priceString.indexOf(currency);
    const currencyValuePosition = currencyCodePosition + currency.length;
    const matchWhitespace = /\s/;
    if (currencyCodePosition >= 0 && currencyValuePosition < priceString.length && !matchWhitespace.test(priceString[`${currencyValuePosition}`])) {
        priceString = priceString.slice(0, currencyValuePosition) + ' ' + priceString.slice(currencyValuePosition, priceString.length);
    }
    return priceString;
}
export function getPriceString({ context, price, countryCode, currency, maximumFractionDigits = 5, formatStringLocalizationKey = '{0}/{1}', isQuantifiable = false, ignoreFreeString = false, isOneTimePaymentOffer = false, locale, offerType }) {
    if (!price || typeof price !== 'object') {
        return '';
    }
    let text = '';
    if (price.value === 0 && !ignoreFreeString) {
        text = context.loc('Pricing_Free');
    } else {
        let unitLocalizationKey = '';
        if (price.unit.toLowerCase() === 'hr' || price.unit.toLowerCase() === 'hour' || price.unit.toLowerCase() === 'hours') {
            unitLocalizationKey = 'Pricing_Hour';
        } else if (price.unit.toLowerCase() === 'month') {
            unitLocalizationKey = 'Pricing_Month';
        } else if (price.unit.toLowerCase() === 'year') {
            unitLocalizationKey = 'Pricing_Year';
        } else if (price.unit.toLowerCase() === 'perpetual') {
            unitLocalizationKey = '';
        } else {
            unitLocalizationKey = price.unit;
        }
        if (isQuantifiable && unitLocalizationKey && offerType !== OfferType.VirtualMachine) {
            unitLocalizationKey = `User_${unitLocalizationKey}`;
        }
        if (countryCode && countryCode.toLowerCase() === 'dk') {
            countryCode = 'CA';
        }
        const priceString = getPriceWithCurrencySymbol(price.value, countryCode, currency, maximumFractionDigits, locale);
        if (isOneTimePaymentOffer) {
            return priceString;
        }
        if (unitLocalizationKey) {
            text = context.locParams(formatStringLocalizationKey, [
                priceString,
                context.loc(unitLocalizationKey)
            ]);
        } else {
            text = priceString;
        }
    }
    return text;
}
export function getVariablePriceString(sku, countryCode, currency, context, isOneTimePaymentOffer, locale) {
    const returnObj = {
        monthlyPrice: '',
        yearlyPrice: '',
        price: ''
    };
    if (sku.priceVaries) {
        if (sku.offerType === OfferType.SolutionTemplate) {
            returnObj.price = context.loc('CostOfDeployedTemplateComponents');
            return returnObj;
        } else {
            returnObj.price = context.loc('PriceVaries');
            return returnObj;
        }
    }
    if (sku.termPrices) {
        sku.termPrices.forEach((termPrice)=>{
            let termPriceString = getPriceString({
                context,
                price: termPrice,
                countryCode,
                currency: sku.currencyCode || currency,
                locale,
                isQuantifiable: sku.isQuantifiable,
                ignoreFreeString: true,
                isOneTimePaymentOffer
            });
            if (sku.azureInfraCostsExtra) {
                if (sku.offerType !== OfferType.ManagedApp) {
                    returnObj.price = context.locParams('PriceWithAzureInfraCostsExtra', [
                        termPriceString
                    ]);
                    return returnObj;
                }
                // Managed Apps support custom meters, we should show Azure Infra Costs along with custom meters
                termPriceString = context.locParams('PriceWithAzureInfraCostsExtra', [
                    termPriceString
                ]);
            }
            termPriceString = '<span>' + termPriceString + '</span>';
            if (termPrice.customMeter && termPrice.customMeter.length > 0) {
                const customMeterText = getCustomMeterPricing(context, countryCode, currency, sku, termPrice);
                termPriceString += customMeterText;
            }
            const freeTrialText = getFreeTrialText({
                context,
                asInlineHTML: true,
                hasFreeTrial: termPrice.freeDurationDays > 0,
                freeTrialTerm: termPrice.unit,
                freeDurationDays: termPrice.freeDurationDays
            });
            termPriceString = freeTrialText + termPriceString;
            if (termPrice.unit.toLowerCase() === Constants.TermPeriod.Month) {
                returnObj.monthlyPrice = termPriceString;
            } else if (termPrice.unit.toLowerCase() === Constants.TermPeriod.Year) {
                returnObj.yearlyPrice = termPriceString;
            }
        });
        return returnObj;
    }
    const price = getPriceString({
        context,
        price: sku.startingPrice,
        countryCode,
        currency: sku.currencyCode || currency,
        locale,
        isQuantifiable: sku.isQuantifiable,
        ignoreFreeString: true,
        isOneTimePaymentOffer
    });
    if (sku.azureInfraCostsExtra) {
        returnObj.price = context.locParams('PriceWithAzureInfraCostsExtra', [
            price
        ]);
        return returnObj;
    }
    returnObj.price = price;
    return returnObj;
}
function getDefaultPrice({ loc }) {
    return `<span class='contactPublisherText'>${loc('PriceVaries')}</span>`;
}
export function getFuturePriceVariablePriceString({ countryCode, currency, context, futureSku, isOneTimePaymentOffer, locale }) {
    const futurePriceObj = futureSku && getVariablePriceString(futureSku, countryCode, currency, context, isOneTimePaymentOffer, locale);
    const futureMonthlyPrice = futurePriceObj === null || futurePriceObj === void 0 ? void 0 : futurePriceObj.monthlyPrice;
    const futureYearlyPrice = futurePriceObj === null || futurePriceObj === void 0 ? void 0 : futurePriceObj.yearlyPrice;
    return {
        futurePriceMonthly: futureMonthlyPrice ? {
            price: futureMonthlyPrice,
            startDate: futureSku === null || futureSku === void 0 ? void 0 : futureSku.startDate
        } : null,
        futurePriceYearly: futureYearlyPrice ? {
            price: futureYearlyPrice,
            startDate: futureSku === null || futureSku === void 0 ? void 0 : futureSku.startDate
        } : null
    };
}
export function getVariablePriceStringWithDefault({ sku, showPriceColumn, showYearlyPrice, showMonthlyPrice, isMicrosoftManaged, countryCode, currency, context, futureSku, shouldBlockPrices, isOneTimePaymentOffer, locale }) {
    const priceObj = showPriceColumn && sku ? getVariablePriceString(sku, countryCode, currency, context, isOneTimePaymentOffer, locale) : {};
    const monthlyPrice = priceObj === null || priceObj === void 0 ? void 0 : priceObj.monthlyPrice;
    const yearlyPrice = priceObj === null || priceObj === void 0 ? void 0 : priceObj.yearlyPrice;
    let defaultMonthlyPrice = getDefaultPrice(context);
    let defaultYearlyPrice = getDefaultPrice(context);
    const contactPublisherText = getContactPublisherText(context);
    if (isMicrosoftManaged && !monthlyPrice && !yearlyPrice) {
        if (!showMonthlyPrice && showYearlyPrice) {
            defaultYearlyPrice = contactPublisherText;
        } else {
            defaultMonthlyPrice = contactPublisherText;
        }
    }
    // Block prices for non-transactable offer types
    if (shouldBlockPrices) {
        return {
            monthly: monthlyPrice ? contactPublisherText : defaultMonthlyPrice,
            yearly: yearlyPrice ? contactPublisherText : defaultYearlyPrice,
            price: '',
            futurePriceMonthly: null,
            futurePriceYearly: null
        };
    }
    const { futurePriceMonthly, futurePriceYearly } = getFuturePriceVariablePriceString({
        countryCode,
        currency,
        context,
        futureSku,
        isOneTimePaymentOffer,
        locale
    });
    return {
        monthly: monthlyPrice || (showMonthlyPrice ? defaultMonthlyPrice : ''),
        yearly: yearlyPrice || (showYearlyPrice ? defaultYearlyPrice : ''),
        price: priceObj && priceObj.price ? priceObj.price : '',
        futurePriceMonthly,
        futurePriceYearly
    };
}
export function getBillingFrequencyTranslated(context, term, onetimePaymentTranslation = 'Pricing_Onetime_Payment') {
    if (!term) {
        return context.loc(onetimePaymentTranslation, 'one-time payment');
    }
    switch(term){
        case TermsStrings.Term1MString:
        case TermsStrings.MonthString:
            return context.loc('Pricing_Month', 'month');
        case TermsStrings.Term1YString:
        case TermsStrings.YearString:
            return context.loc('Pricing_Year', 'year');
        default:
            return '';
    }
}
export function getBillingTermTranslated({ context, term, useHyphen = true, isSubscription }) {
    switch(term){
        case TermsStrings.Term1MString:
        case TermsStrings.MonthString:
            return !useHyphen ? context.loc('Pricing_Term_1_Month', '1 month') : context.locParams('Pricing_Term_termUnit_termDuration', [
                '1',
                `${isSubscription ? context.loc('Pricing_Month_Subscription', 'month subscription') : context.loc('Pricing_Month', 'month')}`
            ], '1-month');
        case TermsStrings.Term1YString:
        case TermsStrings.YearString:
            return !useHyphen ? context.loc('Pricing_Term_1_Year', '1 year') : context.locParams('Pricing_Term_termUnit_termDuration', [
                '1',
                `${isSubscription ? context.loc('Pricing_Year_Subscription', 'year subscription') : context.loc('Pricing_Year', 'year')}`
            ], '1-year');
        case TermsStrings.Term2YString:
            return !useHyphen ? context.locParams('Pricing_Term_Years', [
                '2'
            ], '2 years') : context.locParams('Pricing_Term_termUnit_termDuration', [
                '2',
                `${isSubscription ? context.loc('Pricing_Year_Subscription', 'year subscription') : context.loc('Pricing_Year', 'year')}`
            ], '2-year');
        case TermsStrings.Term3YString:
            return !useHyphen ? context.locParams('Pricing_Term_Years', [
                '3'
            ], '3 years') : context.locParams('Pricing_Term_termUnit_termDuration', [
                '3',
                `${isSubscription ? context.loc('Pricing_Year_Subscription', 'year subscription') : context.loc('Pricing_Year', 'year')}`
            ], '3-year');
        case TermsStrings.Term4YString:
            return !useHyphen ? context.locParams('Pricing_Term_Years', [
                '4'
            ], '4 years') : context.locParams('Pricing_Term_termUnit_termDuration', [
                '4',
                `${isSubscription ? context.loc('Pricing_Year_Subscription', 'year subscription') : context.loc('Pricing_Year', 'year')}`
            ], '4-year');
        case TermsStrings.Term5YString:
            return !useHyphen ? context.locParams('Pricing_Term_Years', [
                '5'
            ], '5 years') : context.locParams('Pricing_Term_termUnit_termDuration', [
                '5',
                `${isSubscription ? context.loc('Pricing_Year_Subscription', 'year subscription') : context.loc('Pricing_Year', 'year')}`
            ], '5-year');
        default:
            return '';
    }
}
export function getPriceWithFrequencyText({ context, priceObject, isQuantifiable, countryCode, currency, maximumFractionDigits = 5, onetimePaymentTranslation = 'Pricing_Onetime_Payment', locale }) {
    const { unit, billingPlan, value } = priceObject;
    const priceValue = (billingPlan === null || billingPlan === void 0 ? void 0 : billingPlan.price) ? billingPlan.price.listPrice : value;
    const priceText = getPriceWithCurrencySymbol(priceValue, countryCode, currency, maximumFractionDigits, locale);
    const isOnetimePayment = !billingPlan;
    const template = isQuantifiable ? 'Pricing_Per_User_Template' : 'Pricing_Flat_Template';
    if (unit === TermsStrings.Term1MString || unit === TermsStrings.MonthString) {
        const billingFrequency = getBillingTermTranslated({
            context,
            term: unit,
            useHyphen: true,
            isSubscription: false
        });
        return context.locParams(template, [
            priceText,
            billingFrequency
        ]);
    } else if (unit === TermsStrings.Term1YString || unit === TermsStrings.YearString) {
        const billingFrequency = isOnetimePayment ? getBillingTermTranslated({
            context,
            term: unit,
            useHyphen: true,
            isSubscription: false
        }) : context.loc('Pricing_Month', 'month');
        return context.locParams(template, [
            priceText,
            billingFrequency
        ]);
    } else {
        const billingFrequency = getBillingFrequencyTranslated(context, billingPlan === null || billingPlan === void 0 ? void 0 : billingPlan.billingPeriod, onetimePaymentTranslation);
        return context.locParams(template, [
            priceText,
            billingFrequency
        ]);
    }
}
export function getDetailedPriceText(context, countryCode, currency, sku, termPrice, locale) {
    var _termPrice_customMeter;
    const priceWithFrequency = getPriceWithFrequencyText({
        context,
        priceObject: termPrice,
        isQuantifiable: sku.isQuantifiable,
        countryCode,
        currency,
        locale
    });
    let priceText = `<span>${priceWithFrequency}</span>`;
    if ((_termPrice_customMeter = termPrice.customMeter) === null || _termPrice_customMeter === void 0 ? void 0 : _termPrice_customMeter.length) {
        priceText += getCustomMeterPricing(context, countryCode, currency, sku, termPrice);
    }
    const freeTrialText = getFreeTrialText({
        context,
        asInlineHTML: true,
        hasFreeTrial: termPrice.freeDurationDays > 0,
        freeTrialTerm: termPrice.unit,
        freeDurationDays: termPrice.freeDurationDays
    });
    return `${freeTrialText}${priceText}`;
}
export function getFullPriceString({ context, countryCode, currency, priceValue, billingPlan, termDuration, isQuantifiable, hasFreeTrial, locale }) {
    const priceWithFrequency = getPriceWithFrequencyText({
        context,
        priceObject: {
            unit: termDuration,
            billingPlan,
            value: priceValue
        },
        isQuantifiable,
        countryCode,
        currency,
        locale,
        maximumFractionDigits: 5
    });
    // Always sent in 1M free trial as
    const freeTrialText = getFreeTrialText({
        context,
        asInlineHTML: false,
        hasFreeTrial,
        freeTrialTerm: TermsStrings.Term1MString
    });
    return `${freeTrialText}${priceWithFrequency}`;
}
/** Returns ascending order, i.e: 0 is first, then 1, then 2 and so on */ export function getTermPriorityForSorting(termsStrings) {
    const lastPriority = 1000;
    switch(termsStrings){
        case TermsStrings.Term1MString:
        case TermsStrings.MonthString:
            return 0;
        case TermsStrings.Term1YString:
        case TermsStrings.YearString:
            return 1;
        case TermsStrings.Term2YString:
            return 2;
        case TermsStrings.Term3YString:
            return 3;
        case TermsStrings.Term4YString:
            return 4;
        case TermsStrings.Term5YString:
            return 5;
        default:
            return lastPriority;
    }
}
/** Returns a new array of ascendingly sorted term prices. First by billing term and if the billing terms are equal then by the billing period frequency */ export function sortTermPrices(termPrices) {
    if (!(termPrices === null || termPrices === void 0 ? void 0 : termPrices.length)) {
        return [];
    }
    // Cloning before calling ".sort" is a must because ".sort" sorts the array in-place.
    return [
        ...termPrices
    ].sort((a, b)=>{
        if (a.unit === b.unit) {
            var _a_billingPlan, _b_billingPlan;
            return getTermPriorityForSorting((_a_billingPlan = a.billingPlan) === null || _a_billingPlan === void 0 ? void 0 : _a_billingPlan.billingPeriod) - getTermPriorityForSorting((_b_billingPlan = b.billingPlan) === null || _b_billingPlan === void 0 ? void 0 : _b_billingPlan.billingPeriod);
        }
        return getTermPriorityForSorting(a.unit) - getTermPriorityForSorting(b.unit);
    });
}
export const addFuturePricing = (billingTermPeriod, monthlyFuturePrice, yearlyFuturePrice)=>{
    if ((billingTermPeriod === TermsStrings.Term1MString || billingTermPeriod === TermsStrings.MonthString) && monthlyFuturePrice) {
        return /*#__PURE__*/ _jsx(FuturePriceTooltip, {
            futurePrice: _object_spread_props(_object_spread({}, monthlyFuturePrice), {
                className: 'checkoutCell ms-fontSize-12'
            })
        });
    } else if ((billingTermPeriod === TermsStrings.Term1YString || billingTermPeriod === TermsStrings.YearString) && yearlyFuturePrice) {
        return /*#__PURE__*/ _jsx(FuturePriceTooltip, {
            futurePrice: _object_spread_props(_object_spread({}, yearlyFuturePrice), {
                className: 'checkoutCell ms-fontSize-12'
            })
        });
    }
    return null;
};
function saasStartsFromPricing({ startingPrice, context, countryCode, maximumFractionDigits = 5, locale, isOneTimePaymentOffer }) {
    const { pricingData, isQuantifiable } = startingPrice;
    if (!pricingData || typeof pricingData !== 'object') {
        return '';
    }
    if (pricingData.value === 0) {
        return context.loc('Pricing_Free');
    }
    const { unit } = pricingData;
    const priceText = getPriceWithFrequencyText({
        context,
        priceObject: pricingData,
        isQuantifiable,
        countryCode,
        currency: pricingData.currency,
        maximumFractionDigits,
        locale
    });
    if (isOneTimePaymentOffer) {
        return priceText;
    }
    const termTranslated = getBillingTermTranslated({
        context,
        term: unit,
        useHyphen: false
    });
    return context.locParams('Pricing_Multiyear_Starts_From_Template', [
        priceText,
        termTranslated
    ]);
}
export function getStartingFromPriceText({ startingPrice, offerType, context, countryCode, startingPriceOptions = {}, isOneTimePaymentOffer, locale }) {
    const { pricingData } = startingPrice;
    const { maximumFractionDigits, formatStringLocalizationKey, ignoreFreeString } = startingPriceOptions;
    if (!pricingData || typeof pricingData !== 'object') {
        return '';
    }
    if (offerType === OfferType.SaaSApp) {
        return saasStartsFromPricing({
            startingPrice,
            context,
            countryCode,
            maximumFractionDigits,
            locale,
            isOneTimePaymentOffer
        });
    } else {
        return getPriceString({
            context,
            price: startingPrice.pricingData,
            countryCode,
            currency: pricingData.currency,
            locale,
            isQuantifiable: startingPrice.isQuantifiable,
            ignoreFreeString,
            isOneTimePaymentOffer,
            formatStringLocalizationKey,
            maximumFractionDigits,
            offerType
        });
    }
}
const canRenderPricing = (app, billingCountryCode)=>{
    const { ctaTypes, licenseManagement, planPricingType } = app;
    // Render plans and pricing only for Transactable Apps and MicrosoftManaged offers
    return planPricingType === PlanPricingType.SimplePlanPricing && (isTransactApp({
        isEmbedded: false,
        ctaTypes,
        appData: app,
        billingRegion: billingCountryCode
    }) || (licenseManagement === null || licenseManagement === void 0 ? void 0 : licenseManagement.isMicrosoftManaged));
};
const getTabHref = (tabTitle, app, context)=>{
    const productString = getPrimaryProductUrl(app.primaryProduct);
    const productData = getProductByUrlKey({
        urlKey: productString
    });
    const product = productData ? productData.UrlKey : productString;
    return context.buildHref(getPDPRoute(app.appType), {
        productId: product,
        entityId: app.entityId
    }, {
        tab: tabTitle
    });
};
export const getStartingPriceElement = ({ app, billingCountryCode, startingPrice, context, locale })=>{
    if (startingPrice && startingPrice.pricingData && startingPrice.pricingData !== PricingStates.NoPricingData && startingPrice.pricingData !== PricingStates.NotAvailableInThisRegion && startingPrice.pricingData !== PricingStates.AlwaysAvailable && startingPrice.pricingData !== PricingStates.Loading) {
        const pricingElement = {
            type: null,
            label: '',
            value: '',
            href: ''
        };
        if (startingPrice.pricingData === PricingStates.FreeApp) {
            pricingElement.type = Constants.ButtonType.Label;
            pricingElement.label = context.loc('Pricing');
            pricingElement.value = context.loc('Pricing_Free');
        } else if (startingPrice.pricingData === PricingStates.AdditionalPurchasesRequired) {
            pricingElement.type = Constants.ButtonType.Label;
            pricingElement.label = context.loc('Pricing');
            pricingElement.value = context.loc('AdditionalPurchaseMayBeRequired');
        } else if (startingPrice.pricingData === PricingStates.StartFromFree) {
            // Freemium offers with pricing plans
            pricingElement.type = Constants.ButtonType.Label;
            pricingElement.label = context.loc('Pricing_StartsAt');
            pricingElement.value = context.loc('Pricing_Free');
        } else if (typeof startingPrice.pricingData === 'object') {
            if (canRenderPricing(app, billingCountryCode)) {
                pricingElement.href = getTabHref('PlansAndPrice', app, context);
                pricingElement.type = Constants.ButtonType.Link;
                pricingElement.label = context.loc('StartingAt');
                pricingElement.value = getStartingFromPriceText({
                    startingPrice,
                    offerType: app.offerType,
                    context,
                    countryCode: billingCountryCode,
                    locale
                });
            } else {
                pricingElement.type = Constants.ButtonType.Label;
                pricingElement.label = context.loc('Pricing');
                pricingElement.value = getPriceString({
                    context,
                    price: startingPrice.pricingData,
                    countryCode: billingCountryCode,
                    currency: startingPrice.pricingData.currency,
                    locale
                });
            }
        }
        if (typeof startingPrice.pricingData === 'object' && !canRenderPricing(app, billingCountryCode)) {
            if (pricingElement.value) {
                pricingElement.value = pricingElement.value + ' ' + context.loc('SiteLicenseAlsoAvailable');
            } else {
                pricingElement.type = Constants.ButtonType.Label;
                pricingElement.label = context.loc('Pricing');
                pricingElement.value = context.loc('SiteLicenseAlsoAvailable');
            }
        }
        return pricingElement;
    }
    return null;
};
export const shouldBlockPricing = (app)=>{
    // hasPrices check is necessary to ensure we don't block pricing for valid Dynamics 365 and PBI Visual offers
    return (isD365App(app) || isD365BCApp(app) || isPowerBIVisualsApp(app)) && !isBagTransactable(app) && app.hasPrices;
};
export const bulidStartingPriceElement = ({ app, pricingLinkClassName, billingCountryCode, context, locale })=>{
    const startingPrice = shouldBlockPricing(app) ? null : app.startingPrice;
    const pricingElement = getStartingPriceElement({
        app,
        billingCountryCode,
        startingPrice,
        context,
        locale
    });
    if (pricingElement) {
        return /*#__PURE__*/ _jsx("div", {
            className: "cell",
            itemProp: "offers",
            itemScope: true,
            itemType: "https://schema.org/Offer",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "pricingText",
                children: [
                    pricingElement && pricingElement.label && pricingElement.type && pricingElement.value ? /*#__PURE__*/ _jsx(ButtonLink, {
                        href: pricingElement.href ? pricingElement.href : '',
                        className: pricingLinkClassName,
                        type: pricingElement.type,
                        accEnabled: true,
                        label: pricingElement.label,
                        value: pricingElement.value
                    }) : null,
                    startingPrice.pricingData === PricingStates.FreeApp ? /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("meta", {
                                itemProp: "price",
                                content: "0"
                            }),
                            /*#__PURE__*/ _jsx("meta", {
                                itemProp: "priceCurrency",
                                content: "USD"
                            }),
                            /*#__PURE__*/ _jsx("meta", {
                                itemProp: "category",
                                content: "free"
                            })
                        ]
                    }) : typeof startingPrice.pricingData === 'object' ? /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("meta", {
                                itemProp: "priceCurrency",
                                content: startingPrice.pricingData.currency
                            }),
                            /*#__PURE__*/ _jsx("meta", {
                                itemProp: "price",
                                content: getPriceString({
                                    context,
                                    price: startingPrice.pricingData,
                                    countryCode: billingCountryCode,
                                    currency: startingPrice.pricingData.currency,
                                    locale
                                }).replace(/[^0-9.]/g, '')
                            })
                        ]
                    }) : null
                ]
            })
        });
    }
    return null;
};
export const hidePowerBiVisualPricing = ({ app, isEmbedded })=>{
    return !isEmbedded && isPowerBIVisualsApp(app) && app.hasPrices;
};
